import React, { useState } from "react";
import { Table, Select, Button } from "antd";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  currentRole,
  getProjectInfoById,
  userEmail,
} from "../../../api/apiCallManager";
import useProjectInfoQuery from "../../../components/hooks/useProjectInfoQuery";
import { useParams } from "react-router-dom";
import Loader from "../../../components/spinner/Loader";
import { showError, showSuccess } from "../../../helpers/alerts";

const { Option } = Select;

const VersionConformTable = ({ clientData, handleCancel }) => {
  const [selectedVersions, setSelectedVersions] = useState({}); // State to track selected versions
  const { id } = useParams();
  const queryClient = useQueryClient();
  const {
    data: projectInfoData,
    error: projectInfoErro,
    isLoading: projectInfoLoading,
  } = useProjectInfoQuery(id, getProjectInfoById);
  const handleVersionChange = (clientName, value) => {
    setSelectedVersions((prev) => ({
      ...prev,
      [clientName]: value, // Update the version for the specific client
    }));
  };
  // Define the mutation for the API call
  const conformAllMutation = useMutation({
    mutationFn: async (payload) => {
      const response = await fetch(
        "https://lexhue53xg.execute-api.ap-southeast-1.amazonaws.com/prod/server-progres",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to submit versions");
      }

      return response.json(); // Return the response
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["serverVersions_"] });

      if (data?.statusCode === 200) {
        showSuccess("Version Confirmed Successfully");
        handleCancel();
      }
    },
    onError: (error) => {
      console.error("Submission failed:", error.message);
    },
  });

  const handleConfirm = () => {
    const missingSelections = clientData?.some(
      (client) => !selectedVersions[client.clientName]
    );

    if (missingSelections) {
      // Display an error alert if validation fails
      showError("Please select a version for all clients before confirming.");
      return;
    }

    const payload = {
      event_type: "create_version",
      projectKey: projectInfoData?.project_key,
      clients: clientData.map((client) => ({
        clientName: client.clientName,
        region: client.region,
        version: selectedVersions[client.clientName], // Get the selected version for the client
      })),
      notificationLog: {
        title: "New Server Version Created",
        project_key_value: projectInfoData?.project_key,
        description: null,
        notificationType: "email",
        region: "N.Virginia",
        notificationTo: null,
        createdBy: {
          role: currentRole,
          email: userEmail,
        },
      },
    };
    conformAllMutation.mutate(payload);
  };

  const columns = [
    {
      title: "Client Name",
      dataIndex: "clientName",
      key: "clientName",
    },
    {
      title: "Region",
      dataIndex: "region",
      key: "region",
    },
    {
      title: "Select Version",
      key: "dropdown",
      render: (_, record) => (
        <Select
          placeholder="Select Version"
          style={{ width: 120 }}
          value={selectedVersions[record.clientName]} // Bind the selected value
          onChange={(value) => handleVersionChange(record.clientName, value)} // Handle change
        >
          {record.versions
            .filter((v) => /\.0$/.test(v))
            .map((version, index) => (
              <Option key={index} value={version}>
                {version}
              </Option>
            ))}
        </Select>
      ),
    },
  ];

  return (
    <>
      {/* {JSON.stringify(projectInfoData)} */}
      <Table
        className="user-table-theme"
        dataSource={clientData}
        columns={columns}
        loading={!clientData}
        rowKey="clientName" // Using clientName as the key
      />
      <div className="my-5"></div>
      <button
        disabled={conformAllMutation.isPending}
        className="bg-[#0057b8] w-[100%] text-white text-[13px] transition-[0.4s] py-2 rounded hover:bg-blue-700"
        onClick={handleConfirm}
      >
        <Loader isLoading={conformAllMutation.isPending} />
        Create Server Version
      </button>
    </>
  );
};

export default VersionConformTable;
